import { DropdownMenuItem } from "@/components/ui/baseUi/dropdown-menu"
import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { useBoxProgrammationSidebar } from "@/modules/boxProgrammation/hooks/useBoxProgramSidebar"
import { cn } from "@/modules/lib/utils"
import { EllipsisMenu } from "@/modules/ui/components/ellipsisMenu"
import { PencilIcon } from "@images/svgIcons/pencil"
import { PlusIcon } from "@images/svgIcons/plusIcon"
import { QuadBoxIcon } from "@images/svgIcons/quadBoxIcon"
import { ShowcaseIcon } from "@images/svgIcons/showcaseIcon"
import { TypicalWeekIcon } from "@images/svgIcons/typicalWeekIcon"
import { ViewProgrammationIcon } from "@images/svgIcons/viewProgrammationIcon"
import { useBoxProgram } from "@modules/boxProgrammation/hooks/useBoxProgram"
import { useBoxSlotSidebar } from "@modules/boxSlot/hooks/useBoxSlotSidebar"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { useCalendar } from "@modules/calendar/hooks/useCalendar"
import { word } from "@modules/core/utils/i18n"
import { useBoxNavigate } from "@modules/navigation/hooks/useBoxNavigate"
import { Path } from "@modules/navigation/routes"
import dayjs from "dayjs"
import styled from "styled-components"

export function BoxProgramInformation({
  boxProgramId,
  className,
  ...props
}: {
  boxProgramId: string
  className?: string
} & React.ComponentProps<"div">) {
  const { data: boxProgrammation } = useBoxProgram(boxProgramId)
  const { navigateToProgrammationView, navigateToProgrammationEdition } = useBoxProgrammationSidebar()
  const boxNavigate = useBoxNavigate()
  const { navigateToBoxSlotCreation } = useBoxSlotSidebar()
  const { selectedDate } = useCalendar()
  const canEditActivity = useBoxStaffPermission(BoxStaffPermissions.CREATE_ACTIVITY)
  const canAddSlot = useBoxStaffPermission(BoxStaffPermissions.MANAGE_SLOTS)
  const canManageTypicalWeek = useBoxStaffPermission(BoxStaffPermissions.MANAGE_TYPICAL_WEEK)
  const canManageProgramming = useBoxStaffPermission(BoxStaffPermissions.MANAGE_PROGRAMMING)

  const onAddEventClick = () => {
    if (selectedDate) {
      navigateToBoxSlotCreation(boxProgrammation.id, dayjs().hour(8).minute(0).second(0).millisecond(0).toDate())
    }
  }

  return (
    <div
      className={cn(
        "relative mb-5 flex h-50 max-h-50 grow flex-col justify-between gap-6 overflow-hidden rounded-4xl bg-linear-to-r from-secondary-100 to-secondary-200 p-4.5 text-textDefault-body md:p-2 xl:p-4",
        className,
      )}
      {...props}
    >
      {boxProgrammation.image?.url ? (
        <img
          src={boxProgrammation.image.url}
          alt={boxProgrammation.name}
          className="pointer-events-none absolute inset-0 z-0 h-full w-full object-cover opacity-15"
          loading="lazy"
          draggable={false}
        />
      ) : null}

      <div className="z-10 flex flex-row items-center justify-between gap-6">
        <div className="flex flex-row items-center gap-1">
          <div className="mr-2 w-max">
            <QuadBoxIcon color={boxProgrammation.color} />
          </div>
          {boxProgrammation.name.length > 24 ? (
            <Tooltip>
              <TooltipTrigger
                render={
                  <Text fontStyle="TM" className="max-w-40 truncate text-textDefault-body">
                    {boxProgrammation.name}
                  </Text>
                }
              />
              <TooltipContent side="bottom" className="w-fit max-w-60 wrap-break-word whitespace-normal">
                {boxProgrammation.name}
              </TooltipContent>
            </Tooltip>
          ) : (
            <Text fontStyle="TM" className="wrap-break-word whitespace-break-spaces text-textDefault-body">
              {boxProgrammation.name}
            </Text>
          )}
        </div>
        <EllipsisMenu propsContent={{ side: "bottom" }}>
          <DropdownMenuItem
            key="showcase"
            onClick={() => {
              navigateToProgrammationView(boxProgrammation.id)
            }}
          >
            <ShowcaseIcon color="currentColor" width={20} className="text-textDefault-body" />
            {word("box.programmation.activity.activityShowcase")}
          </DropdownMenuItem>
          {canEditActivity && (
            <DropdownMenuItem
              key="edit"
              onClick={() => {
                navigateToProgrammationEdition(boxProgrammation.id)
              }}
            >
              <PencilIcon color="currentColor" width={20} className="text-textDefault-body" />
              {word("global.edit")}
            </DropdownMenuItem>
          )}
        </EllipsisMenu>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex flex-row justify-between gap-2">
          {/* PERMISSIONS - Head-coach & Coach & Financial permissions denied */}
          {canAddSlot && (
            <ActionButton onClick={() => onAddEventClick()}>
              <Round>
                <PlusIcon color="currentColor" width={20} className="text-textDefault-body" />
              </Round>
              <Text fontStyle="LM">{word("box.programmation.activity.newSlot")}</Text>
            </ActionButton>
          )}

          {canManageTypicalWeek && (
            <ActionButton onClick={() => boxNavigate(Path.Box.ProgrammationTemplate(boxProgrammation.id))}>
              <Round>
                <TypicalWeekIcon color="currentColor" width={20} className="text-textDefault-body" />
              </Round>
              <Text fontStyle="LM">{word("box.programmation.activity.typicalWeek")}</Text>
            </ActionButton>
          )}
        </div>
        <div className="flex flex-row justify-between gap-2">
          {canManageProgramming && (
            <ActionButton
              onClick={() =>
                boxNavigate(Path.Box.ProgrammationSessions(boxProgrammation.id), {
                  state: { selectedDate: selectedDate?.toString() ?? "" },
                })
              }
            >
              <Round>
                <ViewProgrammationIcon color="currentColor" width={20} className="text-textDefault-body" />
              </Round>
              <Text fontStyle="LM">{word("box.programmation.activity.viewProgrammation")}</Text>
            </ActionButton>
          )}
        </div>
      </div>
    </div>
  )
}

const Round = styled.div`
  display: flex;
  width: 30px;
  height: 30px;
  padding: 5px;
  align-items: center;
  gap: 11px;
  border-radius: 25px;
  background-color: var(--primary-500);
`

const ActionButton = styled.div`
  gap: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  padding: 5px;
  z-index: 1;

  &:hover {
    background: color-mix(in oklch, var(--textDefault-body) 30%, transparent);
    border-radius: 25px;
    padding: 5px;
  }
`
